import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { toast } from 'sonner';
import { DialogForm } from '../../../../shared/components/common';
import { useCreateSequence } from '../../hooks/useSequences';
import { useApiError } from '../../../../shared/hooks/useApiError';

interface SequenceQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  description: string;
}

export function SequenceQuickCreateDialog({ open, onClose }: SequenceQuickCreateDialogProps) {
  const { t } = useTranslation(['pipeline', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateSequence();
  const { handleMutationError } = useApiError({ showToast: false });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({ name: '', description: '' });
    }
  }, [open, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const sequence = await createMutation.mutateAsync({
        name: data.name,
        description: data.description || undefined,
      });
      toast.success(t('prospecting.sequenceCreated'));
      onClose();
      navigate(`/pipeline/prospecting/sequences/${sequence.id}`);
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('prospecting.createSequence')}
      submitLabel={t('common:app.create')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={createMutation.isPending}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">{t('prospecting.sequenceName')}</Label>
          <Input
            id="name"
            {...register('name', { required: true })}
            className={errors.name ? 'border-destructive' : ''}
            autoFocus
          />
        </div>
        <div>
          <Label htmlFor="description">{t('prospecting.sequenceDescription')}</Label>
          <Textarea
            id="description"
            rows={3}
            {...register('description')}
          />
        </div>
      </div>
    </DialogForm>
  );
}
